import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createSubmission } from "@/lib/api";

const inputCls =
  "w-full rounded-xl border border-white/15 bg-black/50 px-4 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-blaze focus:outline-none";

const EMPTY = {
  organizer_name: "",
  email: "",
  event_name: "",
  category: "festival",
  city: "",
  venue: "",
  event_date: "",
  sale_start: "",
  website: "",
  message: "",
};

export const OrganizerModal = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await createSubmission({
        organizer_name: form.organizer_name,
        email: form.email,
        event_name: form.event_name,
        category: form.category,
        city: form.city || null,
        venue: form.venue || null,
        event_date: form.event_date || null,
        sale_start: form.sale_start || null,
        website: form.website || null,
        message: form.message || null,
      });
      toast.success("Event ingediend!", {
        description: "We nemen zo snel mogelijk contact met je op.",
      });
      setForm(EMPTY);
      onClose();
    } catch {
      toast.error("Indienen mislukt. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="organizer-modal"
        className="max-h-[85vh] overflow-y-auto border-white/10 bg-[#12121C] text-white sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Meld je kaartverkoop aan</DialogTitle>
          <DialogDescription className="text-slate-400">
            Organiseer je een event? Zet je kaartverkoop gratis in de spotlight.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              required
              value={form.organizer_name}
              onChange={set("organizer_name")}
              placeholder="Naam organisatie *"
              data-testid="organizer-name-input"
              className={inputCls}
            />
            <input
              required
              type="email"
              value={form.email}
              onChange={set("email")}
              placeholder="E-mailadres *"
              data-testid="organizer-email-input"
              className={inputCls}
            />
          </div>
          <input
            required
            value={form.event_name}
            onChange={set("event_name")}
            placeholder="Naam event *"
            data-testid="organizer-event-name-input"
            className={inputCls}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <input
              required
              value={form.city}
              onChange={set("city")}
              placeholder="Stad *"
              data-testid="organizer-city-input"
              className={inputCls}
            />
            <input
              value={form.venue}
              onChange={set("venue")}
              placeholder="Locatie/venue"
              data-testid="organizer-venue-input"
              className={inputCls}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <select
              value={form.category}
              onChange={set("category")}
              data-testid="organizer-category-select"
              className={inputCls}
            >
              <option value="festival">Festival</option>
              <option value="concert">Concert</option>
              <option value="sport">Sport & Fit</option>
              <option value="club">Rave & Indoor</option>
              <option value="voetbal">Voetbal</option>
            </select>
            <input
              value={form.website}
              onChange={set("website")}
              placeholder="Website (https://...)"
              data-testid="organizer-website-input"
              className={inputCls}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">
                Eventdatum
              </span>
              <input
                type="datetime-local"
                value={form.event_date}
                onChange={set("event_date")}
                data-testid="organizer-event-date-input"
                className={inputCls}
              />
            </label>
            <label className="block">
              <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-slate-500">
                Start kaartverkoop
              </span>
              <input
                type="datetime-local"
                value={form.sale_start}
                onChange={set("sale_start")}
                data-testid="organizer-sale-start-input"
                className={inputCls}
              />
            </label>
          </div>
          <textarea
            value={form.message}
            onChange={set("message")}
            placeholder="Extra informatie (optioneel)"
            rows={3}
            data-testid="organizer-message-input"
            className={inputCls}
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="organizer-submit-event-button"
            className="w-full rounded-full bg-blaze py-3 text-sm font-bold text-black transition-transform hover:scale-[1.02] hover:bg-blaze-hover disabled:opacity-50"
          >
            {loading ? "Versturen..." : "Verstuur aanmelding"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
